import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type {
  AggregatorDirection,
  AggregatorDirectionCreate,
  AggregatorDirectionUpdate,
} from '../types';
import { normalizeEncodingMode } from '../../constants/encodingMode';

function withKey(item: AggregatorDirection): AggregatorDirection {
  return {
    ...item,
    id: item.direction_id ?? item.id ?? null,
  };
}

function resolveCurrency(
  value: string | null | undefined,
  fallback: string | null | undefined = 'RUB',
): string {
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (typeof fallback === 'string' && fallback.trim() !== '') return fallback.trim();
  return 'RUB';
}

export function createAggregatorDirectionsAdapter(
  aggregatorId: number,
): CrudAdapter<AggregatorDirection, AggregatorDirectionCreate, AggregatorDirectionUpdate> {
  async function byKey(id: number | string): Promise<AggregatorDirection> {
    const { data, error } = await smsClient.GET(
      '/aggregators/{aggregator_id}/directions/{direction_id}',
      {
        params: {
          path: { aggregator_id: aggregatorId, direction_id: Number(id) },
        },
      },
    );
    if (error) throw error;
    return withKey(data as AggregatorDirection);
  }

  return {
    async list({ offset, limit, sortName, sortDirection, filter }) {
      const { data, error } = await smsClient.GET('/aggregators/{aggregator_id}/directions/', {
        params: {
          path: { aggregator_id: aggregatorId },
          query: {
            offset,
            limit,
            'sort.name': sortName ?? 'direction_id',
            'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
            ...(filter?.currency != null ? { currency: String(filter.currency) } : {}),
          },
        },
      });
      if (error) throw error;
      return {
        items: (data?.items ?? []).map(withKey),
        total: data?.total ?? 0,
      };
    },
    async create(payload) {
      const body: AggregatorDirectionCreate = {
        ...payload,
        aggregator_id: aggregatorId,
        currency: resolveCurrency(payload.currency),
        encoding_mode: normalizeEncodingMode(payload.encoding_mode),
      };
      const { data, error } = await smsClient.POST('/aggregators/{aggregator_id}/directions/', {
        params: { path: { aggregator_id: aggregatorId } },
        body: body as AggregatorDirectionCreate,
      });
      if (error) throw error;
      const directionId = data?.direction_id ?? payload.direction_id;
      return { id: directionId };
    },
    async update(payload) {
      const directionId = payload.direction_id ?? payload.id;
      if (directionId == null) throw new Error('direction_id required');

      // DevExtreme CustomStore.update sends only changed fields.
      const existing = await byKey(directionId).catch(() => null);

      const body: AggregatorDirectionUpdate = {
        direction_id: Number(directionId),
        currency: resolveCurrency(payload.currency, existing?.currency),
        aggregator_id: aggregatorId,
        encoding_mode:
          payload.encoding_mode !== undefined
            ? normalizeEncodingMode(payload.encoding_mode)
            : normalizeEncodingMode(existing?.encoding_mode),
      };
      const { data, error } = await smsClient.PUT('/aggregators/{aggregator_id}/directions/', {
        params: { path: { aggregator_id: aggregatorId } },
        body: body as AggregatorDirectionUpdate,
      });
      if (error) throw error;
      return withKey((data ?? body) as AggregatorDirection);
    },
    byKey,
    async remove(id) {
      const { error } = await smsClient.DELETE(
        '/aggregators/{aggregator_id}/directions/{direction_id}',
        {
          params: {
            path: { aggregator_id: aggregatorId, direction_id: Number(id) },
          },
        },
      );
      if (error) throw error;
    },
  };
}

export function createAggregatorDirectionsStore(aggregatorId: number) {
  return useCrudDataSource(createAggregatorDirectionsAdapter(aggregatorId), {
    keyExpr: 'direction_id',
    defaultPageSize: 100,
  });
}
